import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../LanguageContext';
import { FaSolarPanel, FaLeaf, FaBolt } from 'react-icons/fa';

const Hero = () => {
  const { t } = useLanguage();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 },
    },
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Animated background */}
      <div className="absolute inset-0 hero-gradient" />
      
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img
          src="/images/hero.jpg"
          alt="Solar Farm"
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/60" />
      </div>

      {/* Floating sun rays animation */}
      <div className="sun-rays absolute -top-20 -right-20" />
      <div className="sun-rays absolute -bottom-20 -left-20" style={{ animationDelay: '5s', animationDirection: 'reverse' }} />
      <div className="sun-rays absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] opacity-30" style={{ animationDelay: '2s' }} />

      {/* Content */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 container mx-auto px-4 text-center text-white"
      >
        <motion.div variants={itemVariants} className="mb-6">
          <motion.div
            animate={{
              rotate: 360,
              scale: [1, 1.2, 1],
            }}
            transition={{
              rotate: { duration: 20, repeat: Infinity, ease: "linear" },
              scale: { duration: 2, repeat: Infinity, ease: "easeInOut" },
            }}
            className="inline-block"
          >
            <FaSolarPanel className="text-7xl text-yellow-300 drop-shadow-2xl" />
          </motion.div>
        </motion.div>

        <motion.h1
          variants={itemVariants}
          className="text-5xl md:text-7xl font-bold mb-6 drop-shadow-lg"
          data-testid="hero-title"
        >
          {t.hero.title}
        </motion.h1>

        <motion.p
          variants={itemVariants}
          className="text-xl md:text-2xl mb-4 max-w-4xl mx-auto drop-shadow"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.p
          variants={itemVariants}
          className="text-lg md:text-xl mb-8 max-w-3xl mx-auto text-white/90 drop-shadow"
        >
          {t.hero.description}
        </motion.p>

        <motion.div
          variants={itemVariants}
          className="max-w-2xl mx-auto"
        >
          <h3 className="text-xl font-semibold mb-4">{t.hero.focus}</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: FaSolarPanel, text: t.hero.focus1 },
              { icon: FaLeaf, text: t.hero.focus2 },
              { icon: FaBolt, text: t.hero.focus3 },
            ].map((item, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05, y: -5 }}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20"
                data-testid={`focus-item-${index}`}
              >
                <item.icon className="text-4xl mb-3 mx-auto text-yellow-300" />
                <p className="text-sm md:text-base">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="mt-12"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="inline-block cursor-pointer"
            onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
          >
            <div className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-2">
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="w-1 h-2 bg-white rounded-full"
              />
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;